import {
  useCallback,
  useEffect,
  useState,
  createContext,
  useContext,
} from "react";
import { useLocation, Outlet } from "react-router-dom";
import SalesSideNav from "./SalesSideNav";
import SalesTopNav from "./SalesTopNav";
import { Box, useMediaQuery } from "@mui/material";
import { alpha, styled } from "@mui/material/styles";
import SalesSessionWillExpireBox from "../../modals/SalesSessionWillExpireBox";
import { useSalesSession } from "../../hoc/SalesAuthWrapper";

const SIDE_NAV_WIDTH = 280;

// Context to share nav state with modals
const SalesNavContext = createContext({
  isNavOpen: false,
  sideNavWidth: SIDE_NAV_WIDTH,
});

export const useSalesNav = () => useContext(SalesNavContext);

const LayoutRoot = styled("div")(({ theme, openNav, forceHideSideNav }) => ({
  display: "flex",
  flex: "1 1 auto",
  maxWidth: "100%",
  [theme.breakpoints.up("lg")]: {
    paddingLeft: forceHideSideNav ? 0 : SIDE_NAV_WIDTH,
  },
}));

const LayoutContainer = styled("div")({
  display: "flex",
  flex: "1 1 auto",
  flexDirection: "column",
  width: "100%",
});

export const SalesLayout = () => {
  const { pathname } = useLocation();
  const [openNav, setOpenNav] = useState(false);
  const [forceHideSideNav, setForceHideSideNav] = useState(false);
  const [forceHideAppChrome, setForceHideAppChrome] = useState(false);
  const lgUp = useMediaQuery((theme) => theme.breakpoints.up("lg"));
  const { willExpireSoon, setWillExpireSoon } = useSalesSession();

  // On desktop, nav is always open (permanent drawer)
  // On mobile, nav is open when openNav is true
  const isNavOpen = lgUp ? !forceHideSideNav : openNav;

  const handlePathnameChange = useCallback(() => {
    if (openNav) {
      setOpenNav(false);
    }
  }, [openNav]);

  // Allow other parts of the app to request closing the side nav
  useEffect(() => {
    const handleCloseSideNav = () => setOpenNav(false);
    window.addEventListener("closeSideNav", handleCloseSideNav);
    return () => window.removeEventListener("closeSideNav", handleCloseSideNav);
  }, []);

  // Allow forcing the side nav hidden
  useEffect(() => {
    const handleForceOn = () => setForceHideSideNav(true);
    const handleForceOff = () => setForceHideSideNav(false);
    window.addEventListener("forceHideSideNavOn", handleForceOn);
    window.addEventListener("forceHideSideNavOff", handleForceOff);
    return () => {
      window.removeEventListener("forceHideSideNavOn", handleForceOn);
      window.removeEventListener("forceHideSideNavOff", handleForceOff);
    };
  }, []);

  // Allow forcing TopNav/Footer hidden
  useEffect(() => {
    const handleChromeOn = () => setForceHideAppChrome(true);
    const handleChromeOff = () => setForceHideAppChrome(false);
    window.addEventListener("forceHideAppChromeOn", handleChromeOn);
    window.addEventListener("forceHideAppChromeOff", handleChromeOff);
    return () => {
      window.removeEventListener("forceHideAppChromeOn", handleChromeOn);
      window.removeEventListener("forceHideAppChromeOff", handleChromeOff);
    };
  }, []);

  useEffect(
    () => {
      handlePathnameChange();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pathname]
  );

  return (
    <SalesNavContext.Provider
      value={{ isNavOpen, sideNavWidth: SIDE_NAV_WIDTH }}
    >
      <SalesSessionWillExpireBox
        willExpireSoon={willExpireSoon}
        setWillExpireSoon={setWillExpireSoon}
      />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
        }}
      >
        {!forceHideAppChrome && (
          <Box
            component="header"
            sx={{
              backdropFilter: "blur(6px)",
              backgroundColor: (theme) =>
                alpha(theme.palette.background.default, 0.7),
              position: "sticky",
              top: 0,
              left: {
                lg: forceHideSideNav ? 0 : `${SIDE_NAV_WIDTH}px`,
              },
              width: {
                xs: "100%",
                lg: forceHideSideNav
                  ? "100%"
                  : `calc(100% - ${SIDE_NAV_WIDTH}px)`,
              },
              transition: "left 0.3s ease-in-out, width 0.3s ease-in-out",
              zIndex: (theme) => theme.zIndex.appBar,
            }}
          >
            <SalesTopNav
              onNavOpen={() => setOpenNav(true)}
              togleNav={() => setOpenNav(!openNav)}
              openNav={openNav}
              sideNavWidth={SIDE_NAV_WIDTH}
            />
          </Box>
        )}

        <Box sx={{ display: "flex", flexGrow: 1 }}>
          {!forceHideSideNav && (
            <SalesSideNav
              onClose={() => setOpenNav(false)}
              open={openNav}
              SIDE_NAV_WIDTH={SIDE_NAV_WIDTH}
            />
          )}

          <Box
            component="main"
            sx={{
              flexGrow: 1,
              overflowY: "hidden",
              backgroundColor: "#edf1f5",
            }}
          >
            <LayoutRoot openNav={openNav} forceHideSideNav={forceHideSideNav}>
              <LayoutContainer
                sx={{
                  maxWidth: "1800px",
                  mx: "auto",
                  zIndex: 0,
                }}
              >
                <Outlet />
              </LayoutContainer>
            </LayoutRoot>
          </Box>
        </Box>
      </Box>
    </SalesNavContext.Provider>
  );
};
